import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { insightBySlug, insights } from "@/lib/data/insights";
import useSEO from "@/hooks/useSEO";
import RevealText from "@/components/RevealText";
import { withCanadaKeywords } from "@/lib/data/canada-keywords";
import { ArrowUpRight } from "lucide-react";

export default function InsightDetail() {
  const { slug } = useParams();
  const insight = insightBySlug(slug);

  useSEO({
    title: insight ? `${insight.title} — Manitoba Security Services` : "Insight",
    description: insight ? insight.excerpt : undefined,
    keywords: insight ? withCanadaKeywords(insight.keywords) : undefined,
    canonical:
      typeof window !== "undefined"
        ? window.location.origin + `/insights/${slug}`
        : `/insights/${slug}`,
    jsonLd: insight
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: insight.title,
          datePublished: insight.published,
          description: insight.excerpt,
          author: { "@type": "Organization", name: "Manitoba Security Services" },
          publisher: {
            "@type": "Organization",
            name: "Manitoba Security Services",
          },
          keywords: (insight.keywords || []).join(", "),
        }
      : undefined,
  });

  if (!insight) return <Navigate to="/insights" replace />;

  const related = insights.filter((i) => i.slug !== insight.slug).slice(0, 3);

  return (
    <div className="bg-[#050505] pt-40 md:pt-48">
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 pb-12 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-3">
          <p className="font-editorial italic text-[#FF3B30] text-sm">Insight</p>
          <div className="mt-6 space-y-2">
            <p className="font-body uppercase tracking-[0.14em] text-[#a0a0a0] text-[10px]">
              Category
            </p>
            <p className="font-display uppercase tracking-tight text-white text-sm">
              {insight.category}
            </p>
            <p className="mt-4 font-body uppercase tracking-[0.14em] text-[#a0a0a0] text-[10px]">
              Read time
            </p>
            <p className="font-display uppercase tracking-tight text-white text-sm">
              {insight.readTime}
            </p>
          </div>
        </div>
        <div className="col-span-12 md:col-span-9">
          <RevealText
            lines={[insight.title]}
            className="font-display font-black uppercase tracking-tight leading-[1.05] text-white text-3xl md:text-5xl lg:text-6xl"
            delay={0.05}
          />
          <p className="mt-6 font-editorial italic text-[#a0a0a0] text-lg md:text-2xl max-w-3xl">
            {insight.excerpt}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 md:px-10 py-16 md:py-24 space-y-16">
        {insight.body.map((sec, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-12 gap-6 md:gap-10"
          >
            <div className="col-span-12 md:col-span-4">
              <p className="font-editorial italic text-[#FF3B30] text-sm">
                {String(i + 1).padStart(2, "0")}
              </p>
              <h2 className="mt-4 font-display font-black uppercase tracking-tight text-white text-2xl md:text-3xl leading-tight">
                {sec.h}
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-6">
              <p className="font-body text-base md:text-lg text-[#a0a0a0] leading-[1.75] max-w-2xl">
                {sec.p}
              </p>
            </div>
          </motion.div>
        ))}
      </section>

      <section className="border-y border-[#1e1e1e] bg-[#0b0b0b] py-12">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <p className="font-editorial italic text-[#FF3B30] text-sm mb-4">
            Directory tags
          </p>
          <ul className="flex flex-wrap gap-x-4 gap-y-2">
            {(insight.keywords || []).map((k) => (
              <li
                key={k}
                className="font-body text-xs md:text-sm uppercase tracking-[0.14em] text-[#a0a0a0] border border-[#2a2a2a] px-3 py-1"
              >
                {k}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 md:px-10 py-16 md:py-20">
        <p className="font-editorial italic text-[#FF3B30] text-sm mb-8">
          Continue reading
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {related.map((r) => (
            <Link
              key={r.slug}
              to={`/insights/${r.slug}`}
              className="group border border-[#1e1e1e] hover:border-[#FF3B30] p-6 md:p-8 transition-colors block"
            >
              <p className="font-editorial italic text-[#FF3B30] text-xs">
                {r.category}
              </p>
              <h4 className="mt-2 font-display uppercase tracking-tight text-white text-lg leading-tight group-hover:translate-x-1 transition-transform">
                {r.title}
              </h4>
              <ArrowUpRight
                className="mt-4 text-white/60 group-hover:text-[#FF3B30]"
                size={18}
              />
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
